import { Link } from '@tanstack/react-router'
import type { FormEvent } from 'react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { getTurnstileSiteKey, isPasswordAuthEnabled } from '@/lib/auth-capabilities'
import { authClient, authSocialProviders } from '@/lib/auth-client'
import {
  AuthCard,
  AuthDivider,
  AuthField,
  AuthSecondaryAction,
  AuthSocialButtons,
  AuthSubmit,
  EmailSentState,
  formatAuthProviderName,
} from './shared'
import { TurnstileWidget, type TurnstileWidgetHandle } from './turnstile-widget'

export function SignInForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const passwordAuthEnabled = isPasswordAuthEnabled()
  const turnstileSiteKey = getTurnstileSiteKey()
  const captchaEnabled = Boolean(turnstileSiteKey)
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null)
  const providers = [...authSocialProviders]
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const callbackURL = appOrigin ? `${appOrigin}/` : '/'
  const errorCallbackURL = appOrigin ? `${appOrigin}/auth/sign-in` : '/auth/sign-in'

  const getCaptchaFetchOptions = async () => {
    if (!captchaEnabled) return undefined

    try {
      const token = captchaToken ?? (await turnstileRef.current?.execute())
      if (!token) {
        toast.error('Unable to complete the security check')
        return null
      }

      setCaptchaToken(token)
      return {
        headers: {
          'x-captcha-response': token,
        },
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to complete the security check')
      return null
    }
  }

  const refreshCaptcha = () => {
    if (!captchaEnabled) return
    setCaptchaToken(null)
    turnstileRef.current?.reset()
  }

  const handlePasswordSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!passwordAuthEnabled) {
      await handleMagicLinkSignIn()
      return
    }

    if (!password.trim()) {
      toast.error('Password is required')
      return
    }

    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoading(true)
    const result = await authClient.signIn.email({
      email,
      password,
      callbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoading(false)
    refreshCaptcha()

    if (result.error) {
      toast.error(result.error.message || 'Unable to sign in')
      return
    }

    toast.success('Signed in successfully')
  }

  const handleMagicLinkSignIn = async () => {
    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoading(true)
    const result = await authClient.signIn.magicLink({
      email,
      name: email.split('@')[0] || 'there',
      callbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoading(false)
    refreshCaptcha()

    if (result.error) {
      toast.error(result.error.message || 'Unable to send sign-in link')
      return
    }

    toast.success('Check your inbox for your sign-in link')
    setEmailSent(true)
  }

  const handleSocialSignIn = async (provider: string) => {
    const providerName = formatAuthProviderName(provider)
    const captchaFetchOptions = await getCaptchaFetchOptions()
    if (captchaFetchOptions === null) {
      return
    }

    setLoadingProvider(provider)
    const result = await authClient.signIn.social({
      provider,
      callbackURL,
      errorCallbackURL,
      fetchOptions: captchaFetchOptions,
    })
    setLoadingProvider(null)
    refreshCaptcha()
    if (result.error) {
      toast.error(result.error.message || `Unable to continue with ${providerName}`)
    }
  }

  if (emailSent) {
    return <EmailSentState email={email} onResend={() => setEmailSent(false)} />
  }

  return (
    <AuthCard
      title="Sign in to your route workspace"
      description={
        passwordAuthEnabled
          ? 'Pick up where your booking link and day left off.'
          : 'Enter your email to open the workspace that protects your day.'
      }
    >
      <form className="space-y-4" onSubmit={handlePasswordSignIn}>
        <AuthField
          id="sign-in-email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          placeholder="you@business.com"
        />
        {passwordAuthEnabled && (
          <AuthField
            id="sign-in-password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            placeholder="Your password"
          />
        )}
        {turnstileSiteKey ? (
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            onTokenChange={setCaptchaToken}
          />
        ) : null}
        <AuthSubmit loading={loading || Boolean(loadingProvider)}>
          {passwordAuthEnabled ? 'Sign in' : 'Continue'}
        </AuthSubmit>
        {passwordAuthEnabled ? (
          <>
            <AuthDivider label="or" />
            <AuthSecondaryAction
              loading={loading}
              disabled={Boolean(loadingProvider)}
              onClick={handleMagicLinkSignIn}
            >
              Send a sign-in link
            </AuthSecondaryAction>
            <AuthSocialButtons
              providers={providers}
              disabled={loading || Boolean(loadingProvider)}
              loadingProvider={loadingProvider}
              onProvider={handleSocialSignIn}
            />
          </>
        ) : (
          <>
            <AuthDivider />
            <AuthSocialButtons
              providers={providers}
              disabled={loading || Boolean(loadingProvider)}
              loadingProvider={loadingProvider}
              onProvider={handleSocialSignIn}
            />
          </>
        )}
        <p className="text-center text-sm text-muted-foreground">
          New to Zippity?{' '}
          <Link
            className="px-1 font-medium text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
            to="/auth/$authView"
            params={{ authView: 'sign-up' }}
          >
            Create a workspace
          </Link>
        </p>
      </form>
    </AuthCard>
  )
}
